import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Zap, Eye, EyeOff, ArrowRight, Loader, Sparkles } from 'lucide-react'
import { useAuth } from '@/platform/auth/useAuth'
import { useAuthStore } from '@/shared/store/authStore'
import { ApiError } from '@/shared/types/api.types'
import { Footer } from '@/platform/shell/Footer'

import styles from './AuthPages.module.css'

/**
 * Login page — the entry point to NTTD.
 *
 * After successful login, redirects to the destination stored in
 * location state (set by ProtectedRoute), or defaults to '/'.
 */
export function LoginPage() {
  const { login, demoLogin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/dashboard'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleDemoLogin = () => {
    demoLogin()
    navigate(from === '/' ? '/dashboard' : from, { replace: true })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      await login(email, password)
      navigate(from === '/' ? '/dashboard' : from, { replace: true })
    } catch (err: any) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else if (err?.response?.data?.message) {
        setError(err.response.data.message)
      } else if (err?.message && typeof err.message === 'string' && !err.message.includes('status code')) {
        setError(err.message)
      } else {
        setError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: 'var(--color-bg)' }}>
      <div className={styles.page} style={{ flex: '1 0 auto', minHeight: 'auto' }}>
        {/* Background decoration */}
        <div className={styles.bg} aria-hidden="true">
          <div className={styles.bgGlow} />
        </div>

        <div className={styles.card}>
          {/* Brand */}
          <div className={styles.brand}>
            <div className={styles.logo}>
              <Zap size={22} strokeWidth={2.5} />
            </div>
            <div>
              <h1 className={styles.brandName}>NTTD</h1>
              <p className={styles.brandTagline}>No Time To Delay</p>
            </div>
          </div>

          {/* Header */}
          <div className={styles.header}>
            <h2 className={styles.title}>Welcome back</h2>
            <p className={styles.subtitle}>Sign in to your account to continue</p>
          </div>

          {/* Error */}
          {error && (
            <div className={styles.errorBanner} role="alert">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <div className={styles.field}>
              <label htmlFor="login-email" className={styles.label}>Email</label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                disabled={isLoading}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="login-password" className={styles.label}>Password</label>
              <div className={styles.passwordWrapper}>
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isLoading || !email || !password}
              id="login-submit"
            >
              {isLoading ? (
                <><Loader size={16} className="animate-spin" /> Signing in…</>
              ) : (
                <>Sign in <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <div className={styles.divider}>
            <span>or preview experience</span>
          </div>

          <button
            type="button"
            className={styles.demoBtn}
            onClick={handleDemoLogin}
            id="demo-login-btn"
          >
            <Sparkles size={16} /> Explore as Demo Learner
          </button>

          {/* Footer */}
          <p className={styles.footerText}>
            Don't have an account?{' '}
            <Link to="/register" className={styles.footerLink}>
              Create one
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  )
}
